import { UserTier, planCodeToTier } from "@/lib/tierUtils";
import { UserProfile, UserProfileStatus } from "@/lib/types";
import { markAuthEvent } from "@/shared/api/authEvents";
import { qk } from "@/shared/api/keys";
import { supabase } from "@/shared/api/supabaseClient";
import { isNetworkError } from "@/shared/lib/errorUtils";
import { captureException } from "@/shared/lib/sentry";
import { Session, User, AuthError } from "@supabase/supabase-js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, {
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useMemo,
  useEffect,
  useState,
  useRef,
} from "react";


// --- TYPE DEFINITIONS ---

interface DetailedProfileData {
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  practitioner_id: string | null;
  role_code: string | null;
  practice_name: string | null;
  plan_code: string | null;
  subscription_status: string | null;
  current_period_end: string | null;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  currentUserTier: UserTier | null;
  currentUserRole: string | null;
  currentUserFirstName: string | null;
  currentUserLastName: string | null;
  currentUserPhone: string | null;
  currentUserPractitionerId: string | null;
  activePracticeId: string | null;
  currentUserPracticeName: string | null;
  currentPlanCode: string | null;
  currentSubscriptionStatus: string | null;
  currentSubscriptionPeriodEnd: string | null;
  isReady: boolean;
  login: (
    email: string,
    password: string
  ) => Promise<{ error: AuthError | null }>;
  logout: () => Promise<{ error: AuthError | null }>;
  signUp: (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => Promise<{ user: User | null; error: AuthError | null; requiresEmailConfirmation: boolean }>;
}

// --- CONTEXT CREATION ---

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// --- HELPER FUNCTIONS ---


// Simple in-memory mutex to serialize session refreshes
let refreshInFlight: Promise<Session> | null = null;
const refreshSessionSerialized = async (): Promise<Session> => {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  refreshInFlight = (async () => {
    try {
      const {
        data: { session: refreshedSession },
        error: refreshError,
      } = await supabase.auth.refreshSession();
      if (refreshError || !refreshedSession) {
        const err = new Error("SessionRefreshFailed");
        (err as any).cause =
          refreshError ?? new Error("No session after refresh");
        throw err;
      }
      return refreshedSession;
    } finally {
      // Always clear the mutex to avoid deadlocks
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
};

// Returns the first practice the user is a member of, or null if none.
const pickFirstMembershipPracticeId = async (
  userId: string
): Promise<string | null> => {
  const { data, error } = await supabase
    .from("practice_users")
    .select("practice_id")
    .eq("user_id", userId)
    .limit(1)
    .maybeSingle();

  if (error || !data) {
    return null; // No membership: signals a new user who needs onboarding.
  }

  return data.practice_id;
};

// Sets the active practice claim server-side (validates membership) and
// returns a refreshed session carrying the updated claim.
const applyPracticeClaim = async (practiceId: string): Promise<Session> => {
  const { error: rpcError } = await supabase.rpc("set_active_practice", {
    p_practice_id: practiceId,
  });

  if (rpcError) {
    throw rpcError;
  }

  return refreshSessionSerialized();
};

// Explicit membership check for the current (possibly stale) claim.
// Throws on a real query error so the caller's retry path handles transient
// failures; a missing row simply returns false (the claim is stale).
const isMemberOfPractice = async (
  userId: string,
  practiceId: string
): Promise<boolean> => {
  const { data, error } = await supabase
    .from("practice_users")
    .select("practice_id")
    .eq("user_id", userId)
    .eq("practice_id", practiceId)
    .limit(1)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return !!data;
};

// Picks a practice the user is a member of and applies it as the active claim.
// Returns the refreshed session, or null when the user has no memberships at
// all (caller routes to onboarding). Used both when the claim is missing and
// when it is stale (points to a practice the user no longer belongs to).
const ensurePracticeClaim = async (user: User): Promise<Session | null> => {
  const fallbackPracticeId = await pickFirstMembershipPracticeId(user.id);
  if (!fallbackPracticeId) {
    return null; // No memberships: new user / needs onboarding.
  }

  return applyPracticeClaim(fallbackPracticeId);
};

const fetchDetailedProfile = async (userId: string, practiceId: string) => {
  try {
    const { data, error } = await supabase.rpc(
      "get_user_profile_for_settings",
      {
        p_user_id: userId,
        p_practice_id: practiceId,
      }
    );

    if (error) {
      throw error;
    }
    if (!data) {
      throw new Error("UserProfileNotFound");
    }

    return data as DetailedProfileData;
  } catch (error) {
    throw error;
  }
};

// Isolated queryFn to avoid closure detection issues
const fetchUserProfileQuery = async (): Promise<{ session: Session; profile: UserProfile } | null> => {
  try {
    // Read current session from getSession() to avoid closure over sessionRef
    const { data: { session: currentSession } } = await supabase.auth.getSession();

    if (!currentSession) {
      return null;
    }

    let sessionToUse = currentSession;
    let currentPractice = currentSession.user.app_metadata?.practice_id;

    // Resolve a VALID active-practice claim. Covers two cases:
    //  1. Missing claim → pick the user's first membership.
    //  2. Present-but-stale claim (points to a practice the user is no longer a
    //     member of) → self-heal to a practice they do belong to.
    // The profile RPC does not validate membership for one's own user id, so a
    // stale claim would otherwise silently render a phantom practice and break
    // downstream calls (e.g. team invites → 403).
    const claimNeedsResolution =
      !currentPractice ||
      !(await isMemberOfPractice(currentSession.user.id, currentPractice));

    if (claimNeedsResolution) {
      const refreshedSession = await ensurePracticeClaim(currentSession.user);

      if (refreshedSession) {
        sessionToUse = refreshedSession;
        currentPractice = refreshedSession.user.app_metadata.practice_id;
      } else {
        // No membership anywhere → onboarding (preserves existing flow).
        const onboardingError = new Error("RequiresOnboarding");
        (onboardingError as any).session = currentSession;
        throw onboardingError;
      }
    }

    const detailedProfile = await fetchDetailedProfile(
      sessionToUse.user.id,
      currentPractice
    );

    if (!detailedProfile) {
      throw new Error("UserProfileNotFound");
    }

    const planCode = detailedProfile.plan_code ?? null;
    const planTier = planCodeToTier(planCode);
    const fallbackTier = ((sessionToUse.user as any)?.tier ?? null) as UserTier | null;

    const profile: UserProfile = {
      userId: sessionToUse.user.id,
      tier: planTier ?? fallbackTier,
      practiceId: currentPractice,
      role: detailedProfile.role_code,
      firstName: detailedProfile.first_name,
      lastName: detailedProfile.last_name,
      phone: detailedProfile.phone,
      practitionerId: detailedProfile.practitioner_id,
      practiceName: detailedProfile.practice_name,
      planCode,
      subscriptionStatus: detailedProfile.subscription_status ?? null,
      subscriptionCurrentPeriodEnd: detailedProfile.current_period_end ?? null,
    };

    return {
      session: sessionToUse,
      profile,
    };
  } catch (error) {
    throw error;
  }
};

// --- HELPER HOOK ---

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// --- PROVIDER COMPONENT ---

interface AuthProviderProperties {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProperties> = ({
  children,
}) => {
  const queryClient = useQueryClient();

  // Standard Supabase session pattern from docs
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [stablePracticeId, setStablePracticeId] = useState<string | null>(null);

  // Track current session to avoid unnecessary updates
  const sessionRef = useRef<Session | null>(null);

  // Track login intent to allow session updates during actual login
  const loginIntentRef = useRef(false);

  // Track stable practice ID for callbacks
  const stablePracticeIdRef = useRef<string | null>(null);

  // Compute real prerequisites for the query
  const hasSession = !!session;


  const {
    data: queryData,
    error: queryError,
    status: queryStatus,
  } = useQuery<{ session: Session; profile: UserProfile } | null, Error>({
    queryKey: qk.detail("userProfile", "me"),
    enabled: hasSession, // Query handles practice claim setup internally via ensurePracticeClaim
    queryFn: fetchUserProfileQuery,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: (failureCount: number, error_: Error) => {
      // NOTE: These error.message checks are for CONTROL FLOW only (not user display)
      // They determine retry behavior based on typed status codes embedded in Error messages
      // This pattern is acceptable and intentional for auth flow management
      if (
        error_.message === "RequiresOnboarding" ||
        error_.message === "UserProfileNotFound" ||
        error_.message === "SessionRefreshFailed"
      ) {
        return false;
      }
      return failureCount < 3;
    },
    // Uses global refetchOnWindowFocus setting
    // Keep previous data while refetching to avoid transient nulls
    placeholderData: (prev) => prev,
  });


  // Keep session ref in sync with session state
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // --- STATUS MAPPING ---
  const { derivedStatus, sessionFromError } = useMemo<{
    derivedStatus: UserProfileStatus;
    sessionFromError: Session | null;
  }>((): { derivedStatus: UserProfileStatus; sessionFromError: Session | null } => {
    let status: UserProfileStatus;
    let errorSession: Session | null = null;

    switch (queryStatus) {
      case "pending":
        // If we still have previous data (from placeholderData), treat as success
        // Only mark as logged_out after we've checked for existing session
        if (queryData) {
          status = "success";
        } else if (!session) {
          status = sessionLoaded ? "logged_out" : "loading";
        } else {
          // User has session but query is pending
          // Check if query is disabled because user has no practice association
          const hasPractice = !!session.user?.app_metadata?.practice_id;
          if (!hasPractice) {
            // User is authenticated but has no practice - needs onboarding
            status = "requires_onboarding";
          } else {
            // Query is actually loading (enabled but pending)
            status = "loading";
          }
        }
        break;
      case "error":
        // NOTE: These error.message checks are for CONTROL FLOW only (not user display)
        // They map typed status codes to auth flow states
        if (queryError?.message === "RequiresOnboarding") {
          status = "requires_onboarding";
          errorSession = (queryError as any)?.session ?? null;
        } else if (queryError?.message === "SessionRefreshFailed") {
          status = "logged_out";
        } else {
          status = "error";
        }
        break;
      case "success":
        // If we have a session but no queryData yet, we're still loading profile
        status = queryData
          ? "success"
          : (session || sessionFromError) ? "loading" : "logged_out";
        break;
      default:
        status = "loading";
    }

    return { derivedStatus: status, sessionFromError: errorSession };
  }, [queryStatus, queryError, queryData, session, sessionLoaded]);

  // Derive activePracticeId directly from session JWT instead of async query
  const activePracticeId = useMemo(() => {
    // If we have a session, use practiceId from JWT directly
    if (session?.user?.app_metadata?.practice_id) {
      return session.user.app_metadata.practice_id;
    }

    // Fallback for error states that might have session
    if (sessionFromError?.user?.app_metadata?.practice_id) {
      return sessionFromError.user.app_metadata.practice_id;
    }

    return null;
  }, [session, sessionFromError]);

  const isReady =
    derivedStatus === "success" ||
    derivedStatus === "requires_onboarding" ||
    derivedStatus === "logged_out";



  // Update sticky practice id when we have a definitive status (keep for backwards compatibility)
  useEffect(() => {
    if (derivedStatus === "success" && queryData?.profile?.practiceId) {
      setStablePracticeId(queryData.profile.practiceId);
      stablePracticeIdRef.current = queryData.profile.practiceId;
    } else if (
      derivedStatus === "logged_out" ||
      derivedStatus === "requires_onboarding"
    ) {
      setStablePracticeId(null);
      stablePracticeIdRef.current = null;
    }
  }, [derivedStatus, queryData?.profile?.practiceId, stablePracticeId]);

  // Initialize session and subscribe to auth changes (standard Supabase pattern)
  useEffect(() => {
    let mounted = true;
    // Get initial session once on mount
    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (mounted) {
          // Seed ref synchronously to eliminate race condition
          sessionRef.current = data.session;
          setSession(data.session);
          setSessionLoaded(true);
        }
      })
      .catch((error) => {
        // getSession can reject (corrupt/locked persisted session, storage access
        // denied, transient network). Without a .catch() the raw GoTrue/{code,message}
        // rejection floats to window.onunhandledrejection (see Sentry ZERMMI-44).
        // Treat as "no session" and still mark loaded so the app can't hang in the
        // loading state and never reach isReady.
        if (mounted) {
          sessionRef.current = null;
          setSession(null);
          setSessionLoaded(true);
        }
        if (!isNetworkError(error)) {
          captureException(error, { source: "AuthContext.getSession" });
        }
      });

    // Subscribe to auth changes - lightweight callback per Supabase docs
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Mark auth event for downstream debouncing
      markAuthEvent();

      // Compare with current session to determine if anything changed
      const currentSession = sessionRef.current;
      const sessionChanged =
        session &&
        (!currentSession ||
          currentSession.user?.id !== session.user?.id ||
          currentSession.user?.app_metadata?.practice_id !==
            session.user?.app_metadata?.practice_id ||
          currentSession.user?.email !== session.user?.email ||
          currentSession.access_token !== session.access_token ||
          currentSession.expires_at !== session.expires_at);

      // Update session state only when it actually changes or during login intent
      if (event === "SIGNED_OUT") {
        sessionRef.current = null;  // Seed ref synchronously
        setSession(null);
      } else if (session) {
        // Only update if there's login intent OR session actually changed
        const shouldUpdate = loginIntentRef.current || sessionChanged;

        if (shouldUpdate) {
          sessionRef.current = session;  // Seed ref synchronously
          setSession(session);
        }
      }

      // Defer heavy operations outside callback (Supabase best practice)
      setTimeout(() => {
        // Sync Realtime token
        try {
          supabase.realtime.setAuth(session?.access_token ?? null);
        } catch {
          // Silent fail for realtime sync
        }

        // Handle cache management - only for meaningful events
        if (event === "SIGNED_OUT") {
          queryClient.clear();
        } else if (event === "SIGNED_IN") {
          // Check if we actually updated the session for this event
          const actuallyUpdated =
            session && (loginIntentRef.current || sessionChanged);

          if (actuallyUpdated) {
            if (loginIntentRef.current) {
              // Force an immediate fetch so we don't stay in logged_out with null queryData
              queryClient.refetchQueries({
                queryKey: qk.detail("userProfile", "me"),
                exact: true,
              });
            } else {
              // Mark stale for meaningful session changes (not just focus events)
              queryClient.invalidateQueries({
                queryKey: qk.detail("userProfile", "me"),
                exact: true,
                refetchType: "none",
              });
            }
          }
        } else if (event === "TOKEN_REFRESHED") {
          queryClient.invalidateQueries({
            queryKey: qk.detail("userProfile", "me"),
            exact: true,
            refetchType: "none", // Don't immediately refetch, avoid loading states
          });
        } else if (event === "USER_UPDATED") {
          queryClient.invalidateQueries({
            queryKey: qk.detail("userProfile", "me"),
            exact: true,
            refetchType: "none",
          });
        }
        // Ignore INITIAL_SESSION to prevent startup storms
      }, 0);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [queryClient]);

  // Note: Tab focus token refresh is handled automatically by Supabase SDK
  // Manual refresh was causing conflicts and lockups

  const login = useCallback(
    async (email: string, password: string) => {
      // Set login intent to allow SIGNED_IN session updates
      loginIntentRef.current = true;

      try {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });


        // The onAuthStateChange listener will trigger the profile refetch.
        return { error };
      } catch (e) {
        return { error: e as any };
      } finally {
        // Clear login intent after delay to catch the SIGNED_IN event
        setTimeout(() => {
          loginIntentRef.current = false;
        }, 1000);
      }
    },
    [] // Stable function - no dependencies on changing state
  );

  const logout = useCallback(async () => {
    try {
      // Direct signOut call - let Supabase SDK handle timeouts and retries
      const { error: signOutError } = await supabase.auth.signOut();

      if (signOutError) {
        // Clear cache even on error to reset app state
        queryClient.clear();
        return { error: signOutError };
      }

      // Clear QueryClient cache after successful signOut
      queryClient.clear();
      return { error: null };
    } catch (unexpectedError) {
      // Clear cache even on unexpected errors to help reset app state
      queryClient.clear();
      return { error: unexpectedError as any };
    }
  }, [queryClient]);

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      firstName: string,
      lastName: string
    ) => {
      try {
        // Detect browser language and normalize to supported locales (en or fr)
        const browserLang = (navigator.language || 'en').slice(0, 2);
        const locale = browserLang === 'fr' ? 'fr' : 'en'; // Default to 'en' for unsupported languages

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              first_name: firstName,
              last_name: lastName,
              locale, // Add locale for email template localization
            },
          },
        });

        if (error) {
          return { user: null, error, requiresEmailConfirmation: false };
        }

        // Detect email confirmation requirement
        // If user exists but no session, and email is not verified, then confirmation is required
        const requiresEmailConfirmation =
          !!data.user &&
          !data.session &&
          data.user.identities?.[0]?.identity_data?.email_verified === false;

        // The onAuthStateChange listener will trigger the profile refetch (if session exists).
        return { user: data.user, error: null, requiresEmailConfirmation };
      } catch (unexpectedError) {
        return { user: null, error: unexpectedError as any, requiresEmailConfirmation: false };
      }
    },
    [] // No dependencies - this is a stable function
  );

  const value = useMemo(() => {
    const contextValue = {
      session: session ?? sessionFromError, // Use session from state
      user: session?.user ?? sessionFromError?.user ?? null,
      currentUserTier: queryData?.profile?.tier ?? null,
      currentUserRole: queryData?.profile?.role ?? null,
      currentUserFirstName: queryData?.profile?.firstName ?? null,
      currentUserLastName: queryData?.profile?.lastName ?? null,
      currentUserPhone: queryData?.profile?.phone ?? null,
      currentUserPractitionerId: queryData?.profile?.practitionerId ?? null,
      activePracticeId: activePracticeId,
      currentUserPracticeName: queryData?.profile?.practiceName ?? null,
      currentPlanCode: queryData?.profile?.planCode ?? null,
      currentSubscriptionStatus: queryData?.profile?.subscriptionStatus ?? null,
      currentSubscriptionPeriodEnd: queryData?.profile?.subscriptionCurrentPeriodEnd ?? null,
      isReady,
      login,
      logout,
      signUp,
    };


    return contextValue;
  }, [
    session,
    queryData,
    sessionFromError,
    isReady,
    activePracticeId,
    // Functions are stable with empty deps, so no need to include them
    login,
    logout,
    signUp,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
